import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Upload } from "lucide-react";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { Button } from "../../../components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/dialog";
import { useImportUsers } from "../hooks";
import { validateImportUsers } from "../api/users.api";
import type { UserImportResponse, UserImportReviewRow } from "../types/user.types";

interface UserImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported: (result: UserImportResponse) => Promise<void> | void;
}

const profileColumns = [
  "first_name",
  "last_name",
  "country_code",
  "country",
  "zip_code",
  "gender",
  "date_of_birth",
  "language",
  "population_id",
] as const;

function reviewWorkbook(file: File, buffer: ArrayBuffer): UserImportReviewRow[] {
  if (!file.name.toLowerCase().endsWith(".xlsx")) throw new Error("Choose an .xlsx file.");

  const workbook = XLSX.read(buffer, { type: "array" });
  if (workbook.SheetNames.length !== 1 || workbook.SheetNames[0] !== "Users") {
    throw new Error("The workbook must contain only a Users sheet.");
  }

  const values = XLSX.utils.sheet_to_json<unknown[]>(workbook.Sheets.Users, { header: 1, defval: "" });
  const headers = (values[0] ?? []).map((value) => String(value).trim());
  const columnIndex = (name: string) => headers.indexOf(name);
  const emailColumn = columnIndex("email");
  const phoneColumn = columnIndex("phone_number");
  const passwordColumn = columnIndex("password");
  if (emailColumn < 0 || phoneColumn < 0 || passwordColumn < 0) {
    throw new Error("Users must include email, phone_number and password columns.");
  }

  const rows = values.slice(1).flatMap((values, index) => {
    if (!values.some((value) => String(value).trim())) return [];

    const optional = Object.fromEntries(
      profileColumns.map((column) => {
        const value = columnIndex(column) >= 0 ? String(values[columnIndex(column)] ?? "").trim() : "";
        return [column, value || undefined];
      }),
    );
    const email = String(values[emailColumn] ?? "").trim();
    const phone_number = String(values[phoneColumn] ?? "").trim();
    const password = String(values[passwordColumn] ?? "");
    return [{
      row_number: index + 2,
      email,
      phone_number,
      password,
      ...optional,
      errors: [
        ...(email ? [] : ["Email is required."]),
        ...(phone_number ? [] : ["Phone number is required."]),
        ...(password.trim() ? [] : ["Password is required."]),
      ],
    } as UserImportReviewRow];
  });

  if (!rows.length) throw new Error("The Users sheet must contain at least one user.");
  const phoneCounts = new Map<string, number>();
  rows.forEach((row) => phoneCounts.set(row.phone_number, (phoneCounts.get(row.phone_number) ?? 0) + 1));
  rows.forEach((row) => {
    if (row.phone_number && (phoneCounts.get(row.phone_number) ?? 0) > 1) {
      row.errors.push("Phone number is duplicated in the workbook.");
    }
  });
  return rows;
}

export function UserImportDialog({ open, onOpenChange, onImported }: UserImportDialogProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [rows, setRows] = useState<UserImportReviewRow[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [success, setSuccess] = useState<UserImportResponse | null>(null);
  const { importUsers, loading, error, importErrors, reset } = useImportUsers();
  const invalidCount = rows.filter((row) => row.errors.length > 0).length;

  const close = () => {
    if (loading) return;
    setFileName(null);
    setRows([]);
    setFileError(null);
    setSuccess(null);
    reset();
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
      ["first_name", "last_name", "email", "country_code", "phone_number", "country", "zip_code", "gender", "date_of_birth", "language", "population_id", "password"],
      ["Nguyen", "An", "an@example.com", "+84", "0901234567", "Vietnam", "700000", "male", "1995-05-12", "vi", "", "Secret123!"],
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Users");
    XLSX.writeFile(workbook, "user-import-template.xlsx");
  };

  const selectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    reset();
    setSuccess(null);
    if (!file) return;
    try {
      const reviewedRows = reviewWorkbook(file, await file.arrayBuffer());
      const validation = await validateImportUsers(reviewedRows.map(({ errors: _errors, ...row }) => row));
      const errorsByRow = new Map(validation.users.map((item) => [item.row_number, item.errors.map((error) => error.message)]));
      setRows(reviewedRows.map((row) => ({ ...row, errors: [...row.errors, ...(errorsByRow.get(row.row_number) ?? [])] })));
      setFileName(file.name);
      setFileError(null);
    } catch (reason) {
      setRows([]);
      setFileName(null);
      setFileError(reason instanceof Error ? reason.message : "Unable to review this workbook.");
    }
  };

  const submit = async () => {
    if (!rows.length || invalidCount || loading) return;
    try {
      const result = await importUsers(rows.map(({ errors: _errors, ...row }) => row));
      await onImported(result);
      setSuccess(result);
    } catch (reason) {
      setFileError(reason instanceof Error ? reason.message : t("users.import.refresh_failed"));
    }
  };

  const deleteRow = (rowNumber: number) => setRows((currentRows) => currentRows.filter((row) => row.row_number !== rowNumber));

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t("users.import.title")}</DialogTitle>
          <DialogDescription>{t("users.import.description")}</DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="rounded-xl border border-[#B7E1CD] bg-[#F3FBF6] p-4 text-[#0B6B45]">
            <CheckCircle2 className="mr-2 inline h-5 w-5" />
            {t("users.import.success", { count: success.usersCreated })}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button variant="brand-outline" onClick={downloadTemplate}>
                <Download className="h-4 w-4" />
                {t("users.import.download_template")}
              </Button>
              <input ref={inputRef} type="file" accept=".xlsx" className="sr-only" onChange={(event) => void selectFile(event)} />
              <Button variant="brand-secondary" onClick={() => inputRef.current?.click()}>
                <Upload className="h-4 w-4" />
                {t("users.import.choose_file")}
              </Button>
            </div>
            <p className="text-sm text-[#52606D]">{t("users.import.template_profile_hint")}</p>

            {rows.length ? (
              <div className="overflow-hidden rounded-xl border border-[#D5DDE7]">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#D5DDE7] bg-[#F8FAFC] px-4 py-3 text-sm">
                  <span className="font-medium text-[#0F172A]">{fileName}</span>
                  <span className={invalidCount ? "text-[#B42318]" : "text-[#0B6B45]"}>
                    {t("users.import.review_summary", { total: rows.length, invalid: invalidCount })}
                  </span>
                </div>
                <div className="max-h-72 overflow-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-white text-xs font-semibold uppercase tracking-wide text-[#52606D]">
                      <tr>
                        <th className="px-4 py-3">Row</th>
                        <th className="px-4 py-3">Name</th>
                        <th className="px-4 py-3">Email</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E5E7EB]">
                      {rows.map((row) => (
                        <tr key={row.row_number} className={row.errors.length ? "bg-[#FFF6F6]" : "bg-white"}>
                          <td className="px-4 py-3 text-[#52606D]">{row.row_number}</td>
                          <td className="px-4 py-3">{[row.first_name, row.last_name].filter(Boolean).join(" ") || "—"}</td>
                          <td className="px-4 py-3">{row.email || "—"}</td>
                          <td className="px-4 py-3">{row.phone_number || "—"}</td>
                          <td className="px-4 py-3">
                            {row.errors.length ? <span className="text-[#B42318]">{row.errors.join(" ")}</span> : <span className="text-[#0B6B45]">{t("users.import.valid")}</span>}
                          </td>
                          <td className="px-4 py-3"><button type="button" onClick={() => deleteRow(row.row_number)} className="text-sm font-medium text-[#B42318] hover:underline">{t("users.import.delete_row")}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {fileError || error ? <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700"><AlertCircle className="mr-2 inline h-4 w-4" />{fileError ?? error}</div> : null}
            {importErrors.length ? <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{importErrors.map((item) => <p key={`${item.row}-${item.field}`}>Row {item.row}: {item.message}</p>)}</div> : null}
          </div>
        )}

        <DialogFooter>
          <Button variant="brand-outline" onClick={close} disabled={loading}>{success ? t("users.import.done") : t("common.cancel")}</Button>
          {!success ? <Button variant="brand-secondary" onClick={() => void submit()} disabled={!rows.length || invalidCount > 0 || loading}><FileSpreadsheet className="h-4 w-4" />{loading ? t("users.import.importing") : t("users.import.submit")}</Button> : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
